import type { QueryOptions, ApolloClient } from 'apollo-client';
import type { DataProxy } from 'apollo-cache';
import type { GraphQLTaggedNode } from 'relay-runtime';
import type { GraphQLError } from 'graphql';

import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor';
import { isExpectedApolloError } from '@confluence/error-types/entry-points/expectedGraphQlErrorHelper';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode';

import { getOperationName, UNEXPECTED_RESPONSE } from './getOperationName';
import { getQueryName } from './getQueryName';
import { flagQueryPreloadedByNetworkOnly } from './getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery';
import { isUnauthorizedError } from './errors/isUnauthorizedError';
import { isNotFoundError } from './errors/isNotFoundError';
import { isNotFoundSpaceError } from './errors/isNotFoundSpaceError';
import { trackPreloadedQuery } from './developmentQueryBoundaryVerification';

const muteExpectedGraphQLErrors = (result: any) => {
	// Preloading is "fire and forget" operation on a client as well, so we can mute
	// some known errors, but we can't really do anything about them as they're not
	// make it to the Apollo cache anyway and therefore don't pop at where they can
	// be handled.
	const hasErrors = result?.errors?.length > 0;
	result?.errors?.forEach((error: any) => {
		if (
			isUnauthorizedError(error) ||
			isExpectedApolloError(error) ||
			isNotFoundSpaceError(error) ||
			isNotFoundError(error)
		) {
			markErrorAsHandled(error);
		}
	});

	return hasErrors;
};

/**
 * Same as Apollo's query function but only used in preloader functions
 */
export function query<Q, V>({
	query: _query,
	variables,
	/**
	 * How to preload with fresh data correctly.
	 * In preloader: query with fetchPolicy: "network-only"
	 * In component: query getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery or getQueryPropsAndSetFetchPolicyForNetworkOnlyQuery
	 */
	fetchPolicy = 'cache-first',
	errorPolicy = 'all',
	context,
	client = window.GLOBAL_APOLLO_CLIENT,
}: Omit<QueryOptions<V>, 'query'> & {
	// TODO: CCE2EPERF-655 update QueryOptions to queryInterop param type
	query: GraphQLTaggedNode;
	client?: ApolloClient<any>;
}): Promise<{
	data: Q | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> {
	if (!client) {
		// Referencing to the client via global variable to avoid circular dependency
		// This should never happen and will be removed in the future
		return Promise.resolve(UNEXPECTED_RESPONSE);
	}

	const query = toDocumentNode(_query);
	const name = getOperationName(query);
	const queryName = getQueryName(name, variables);

	if (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	) {
		if (window.__PRELOAD_BOUNDARY_NAME__) {
			trackPreloadedQuery(query, variables, window.__PRELOAD_BOUNDARY_NAME__);
		}
	}

	// This is indirect reference to next/packages/graphql/src/links/DevOnlyGraphqlErrorGenerator.ts
	if (process.env.NODE_ENV !== 'production' && (window as any).graphqlOperationController) {
		const operationController = (window as any).graphqlOperationController;
		const { shouldGenerateError } = operationController.onOperationRequested(name);
		if (shouldGenerateError) {
			return Promise.resolve(UNEXPECTED_RESPONSE);
		}
	}

	let single = true;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const queryName = getQueryName(name, variables);
		const { batchQueries } = getSSRRenderInputs();
		if (queryName in (batchQueries || {})) {
			single = false;
		}
	}

	// If the query has already been preloaded with network only
	// Subsequent query with same name and variable should be done with cache-first to avoid duplicate network request
	flagQueryPreloadedByNetworkOnly(queryName, variables, fetchPolicy);

	const queryContext = {
		single,
		...context,
	};

	if (window.__SSR_REACT_STREAMING__) {
		queryContext.preload = true;
		queryContext.originalQuery = query;
	}

	// Save current boundary context
	const currentBoundary = window.__PRELOAD_BOUNDARY_NAME__;
	return client
		.query({
			query,
			variables,
			errorPolicy,
			fetchPolicy,
			context: queryContext,
		})
		.then((result) => {
			// Restore the previous boundary context as soon as the query resolves
			window.__PRELOAD_BOUNDARY_NAME__ = currentBoundary;
			const hasErrors = muteExpectedGraphQLErrors(result);
			return { ...result, hasErrors };
		});
}

export function queryAgg<T, V>(
	options: Omit<QueryOptions<V>, 'query'> & {
		// TODO: CCE2EPERF-655 update QueryOptions to queryInterop param type
		query: GraphQLTaggedNode;
	},
) {
	return query<T, V>({
		...options,
		query: options.query,
		client: window.GLOBAL_APOLLO_AGG_CLIENT,
	});
}

export function writeQuery<T, V>(
	options: DataProxy.WriteQueryOptions<T, V> & { client?: ApolloClient<any> },
) {
	const client = options.client || window.GLOBAL_APOLLO_CLIENT;
	if (client) {
		client.writeQuery(options);
	}
}

export function writeAggQuery<T, V>(options: DataProxy.WriteQueryOptions<T, V>) {
	writeQuery({ ...options, client: window.GLOBAL_APOLLO_AGG_CLIENT });
}
